import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Tooltip from "@radix-ui/react-tooltip";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { groups, pages, type PageId } from "../pages/registry";
import { BusApi } from "../lib/api";
import { qk } from "../lib/query";
import { cn } from "../lib/cn";

const COLLAPSED_KEY = "agent-ui:rail-collapsed";

export function Rail({
  active,
  onSelect,
}: {
  active: PageId;
  onSelect: (id: PageId) => void;
}) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) !== "false"; // default collapsed
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, String(collapsed)); } catch { /* */ }
  }, [collapsed]);

  const { data: busStats } = useQuery({
    queryKey: qk.bus.stats,
    queryFn: BusApi.stats,
    refetchInterval: 8000,
  });

  const badges: Partial<Record<PageId, number>> = {
    "bus-inbox": busStats?.unread ?? 0,
  };

  return (
    <Tooltip.Provider delayDuration={200}>
      <aside
        className={cn(
          "h-screen flex-shrink-0 bg-surface border-r border-border flex flex-col",
          "transition-[width] duration-200",
          collapsed ? "w-14" : "w-60",
        )}
      >
        {/* Brand */}
        <div className={cn("flex items-center", collapsed ? "px-2.5 py-3 justify-center" : "px-3 py-3 gap-2.5")}>
          <div className="size-7 rounded-md bg-accent flex items-center justify-center text-fg-inverse font-bold text-[11px] tracking-wide flex-shrink-0">
            AF
          </div>
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-fg leading-none">Agent Fleet</div>
                <div className="text-[10px] text-fg-muted mt-0.5 font-mono">v2</div>
              </div>
              <button
                onClick={() => setCollapsed(true)}
                className="text-fg-subtle hover:text-fg p-1 rounded-md hover:bg-surface-2"
                aria-label="Collapse sidebar"
              >
                <ChevronLeft className="size-3.5" />
              </button>
            </>
          )}
        </div>

        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            className="mx-auto mb-2 text-fg-subtle hover:text-fg p-1 rounded-md hover:bg-surface-2"
            aria-label="Expand sidebar"
          >
            <ChevronRight className="size-3.5" />
          </button>
        )}

        <nav className="flex-1 overflow-y-auto px-2 pb-3">
          {groups.map((g) => {
            const groupPages = pages.filter((p) => p.group === g.id);
            return (
              <div key={g.id} className="mt-3 first:mt-1">
                {!collapsed && g.label && (
                  <div className="px-2 mb-1 text-[10px] uppercase tracking-wider text-fg-subtle font-semibold">
                    {g.label}
                  </div>
                )}
                <div className={cn("space-y-0.5", collapsed && "space-y-1")}>
                  {groupPages.map((p) => {
                    const Icon = p.icon;
                    const isActive = p.id === active;
                    const badge = badges[p.id] ?? 0;
                    const inner = (
                      <button
                        onClick={() => onSelect(p.id)}
                        className={cn(
                          "w-full flex items-center rounded-md text-sm transition-colors relative ring-focus",
                          collapsed ? "justify-center h-9 w-9 mx-auto" : "gap-2.5 px-2 h-8",
                          isActive
                            ? "bg-surface-2 text-fg font-medium"
                            : "text-fg-muted hover:bg-surface-2 hover:text-fg",
                        )}
                      >
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-accent rounded-r-full" />
                        )}
                        <Icon className={cn("size-4 flex-shrink-0", isActive ? "text-accent" : "text-fg-subtle")} />
                        {!collapsed && (
                          <>
                            <span className="flex-1 text-left truncate">{p.label}</span>
                            {badge > 0 && (
                              <span className="inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-accent text-fg-inverse text-[10px] font-semibold">
                                {badge > 99 ? "99+" : badge}
                              </span>
                            )}
                          </>
                        )}
                        {collapsed && badge > 0 && (
                          <span className="absolute top-0.5 right-0.5 size-1.5 bg-accent rounded-full" />
                        )}
                      </button>
                    );
                    return collapsed ? (
                      <Tooltip.Root key={p.id}>
                        <Tooltip.Trigger asChild>{inner}</Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side="right"
                            sideOffset={8}
                            className="panel-elevated px-2 py-1 text-xs text-fg shadow-md"
                          >
                            {p.label}
                            {badge > 0 && <span className="ml-1 text-accent">{badge}</span>}
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    ) : (
                      <div key={p.id}>{inner}</div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </aside>
    </Tooltip.Provider>
  );
}
